import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Card,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import lightappService from '../../services/lightappService';

const { Option } = Select;

const GROUP_TYPES = [
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'category', label: 'Category' },
  { value: 'featured', label: 'Featured' },
];

const TemplateGroups = () => {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchGroups();
    fetchTemplates();
  }, []);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await lightappService.getTemplateGroups();
      if (response.success) {
        setGroups(response.data?.list || response.data || []);
      } else {
        message.error(response.error || 'Failed to fetch template groups');
      }
    } catch (error) {
      message.error('Failed to fetch template groups');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await lightappService.getTemplates({ page_size: 500 });
      if (response.success) {
        setTemplates(response.data?.list || response.data || []);
      }
    } catch (error) {
      // ignore, select will be empty
    }
  };

  const handleAdd = () => {
    setEditingGroup(null);
    form.resetFields();
    form.setFieldsValue({ type: 'marketplace', is_active: true });
    setModalVisible(true);
  };

  const handleEdit = async (record) => {
    setEditingGroup(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      type: record.type,
      is_active: record.is_active !== false && record.is_active !== 0,
      template_ids: [],
    });
    setModalVisible(true);

    // Load full group detail for its template membership
    try {
      const response = await lightappService.getTemplateGroup(record.id);
      if (response.success) {
        const detail = response.data || {};
        const items = detail.templates || detail.items || [];
        form.setFieldsValue({
          template_ids: items.map((i) => i.template_id),
        });
      }
    } catch (error) {
      // ignore
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await lightappService.deleteTemplateGroup(id);
      if (response.success) {
        message.success('Template group deleted');
        fetchGroups();
      } else {
        message.error(response.error || 'Failed to delete template group');
      }
    } catch (error) {
      message.error('Failed to delete template group');
    }
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      const data = {
        name: values.name,
        description: values.description,
        type: values.type,
        is_active: values.is_active ? 1 : 0,
        template_ids: values.template_ids || [],
      };

      let response;
      if (editingGroup) {
        response = await lightappService.updateTemplateGroup(editingGroup.id, data);
      } else {
        response = await lightappService.createTemplateGroup(data);
      }

      if (response.success) {
        message.success(`Template group ${editingGroup ? 'updated' : 'created'}`);
        setModalVisible(false);
        fetchGroups();
      } else {
        message.error(response.error || 'Failed to save template group');
      }
    } catch (error) {
      message.error('Failed to save template group');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <AppstoreOutlined />
          <span style={{ fontWeight: 500 }}>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (
        <Tag color={active ? 'success' : 'default'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this template group?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="模板分组管理"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Group
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={groups}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No template groups yet. Click “Add Group” to create one."
              />
            ),
          }}
        />
      </Card>

      <Modal
        title={editingGroup ? 'Edit Template Group' : 'Add Template Group'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Group Name"
            rules={[{ required: true, message: 'Please enter a group name' }]}
          >
            <Input placeholder="e.g., Databases" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Please select a type' }]}
          >
            <Select>
              {GROUP_TYPES.map((t) => (
                <Option key={t.value} value={t.value}>
                  {t.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Optional description" />
          </Form.Item>

          <Form.Item name="template_ids" label="Templates">
            <Select
              mode="multiple"
              placeholder="Select templates for this group"
              showSearch
              optionFilterProp="children"
            >
              {templates.map((t) => (
                <Option key={t.id} value={t.id}>
                  {t.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TemplateGroups;
